import { useMemo } from "react";

import { VoiceVisualizer } from "react-voice-visualizer";

import { trackEvent } from "@needl-ai/common/analytics";
import {
  useIsArmadaBranding,
  useIsICRAResearchAssistBranding,
  useIsMSquaredBranding,
  useIsPWCBranding,
} from "@needl-ai/common/branding";
import { TooltipProvider } from "@needl-ai/common/components/ui/tooltip";
import { useFetchFeatureConfig } from "@needl-ai/common/hooks/useFetchFeatureConfig";
import { useSpeechToText } from "@needl-ai/common/hooks/useSpeechToText";

import AskneoServiceSelector from "./AskneoServiceSelector";
import ChatTextarea from "./ChatTextarea";
import SendButton from "./SendButton";
import WebSearchToggle from "./WebSearchToggle";

import { AttachmentButton } from "@/components/ui/AttachmentButton";
import { AttachmentList } from "@/components/ui/AttachmentList";
import { useFileUploadContext } from "@/contexts/FileUploadContext";
import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { useChat } from "@/hooks/useChat";
import { useFetchAskNeedlTools } from "@/hooks/useFetchAskNeedlTools";
import { usePollAttachmentsStatus } from "@/hooks/usePollAttachmentsStatus";
import {
  hasIncompleteAttachments,
  shouldDisableFileUpload,
} from "@/utils/attachmentUtils";
import { SpeechButton } from "@/vendor/SpeechButton";

const ChatInputCard = ({
  value,
  onChange,
  onSend,
  onStop,
  disabled = false,
  isStreaming = false,
  placeholder = "How can I help you today?",
}: ChatInputCardProps) => {
  const { isLoading } = useFetchAskNeedlTools();

  const isICRAResearchAssist = useIsICRAResearchAssistBranding();
  const isPWC = useIsPWCBranding();
  const isMSquared = useIsMSquaredBranding();
  const isArmada = useIsArmadaBranding();

  const { data: featureConfig } = useFetchFeatureConfig();
  const showSpeechToText = featureConfig?.speech_to_text ?? false;
  const showWebSearch = featureConfig?.askneedl_web_search ?? false;
  const showUpload = featureConfig?.ask_needl_upload ?? true;

  const { attachments } = useAskNeedlUpload();
  const { state } = useFileUploadContext();

  const { incrementVoiceCount, sessionId } = useChat();

  const {
    state: speechState,
    start: startSpeech,
    stop: stopSpeech,
    cancel: cancelSpeech,
    recorderControls,
    isError: speechError,
  } = useSpeechToText((text) => {
    trackEvent(
      "voice_input_used",
      sessionId ? { session_id: sessionId } : undefined
    );
    incrementVoiceCount();
    onChange(value ? `${value} ${text}` : text);
  });

  usePollAttachmentsStatus(
    state.attachments,
    state.attachment_feed_id || undefined
  );

  const hasIncompleteFiles = hasIncompleteAttachments(state.attachments);
  const isFileUploadDisabled = shouldDisableFileUpload(
    state.attachments,
    state.attachment_feed_id
  );
  const isSendDisabled = disabled || isStreaming || hasIncompleteFiles;
  const isAttachmentDisabled = isFileUploadDisabled;
  const isRecording = speechState === "recording";

  const primaryColor = useMemo(() => {
    const hsl = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary")
      .trim();
    if (!hsl) return "#367d8d";
    const [h, s, l] = hsl.split(/\s+/);
    return `hsl(${h}, ${s}, ${l})`;
  }, []);

  const getDisabledReason = (): string | undefined => {
    if (isStreaming) return "Please wait while processing...";
    if (disabled) return "Enter a message";
    if (isFileUploadDisabled) return "Waiting for upload to complete...";
    return undefined;
  };

  return (
    <TooltipProvider>
      <div className="w-full max-w-3xl mx-auto">
        <div className="backdrop-blur-sm w-full min-h-[4rem] relative bg-white/95 border-gray-200/75 shadow-lg dark:bg-neutral-900/95 dark:border-neutral-800">
          {attachments.length > 0 && !isRecording && <AttachmentList />}

          <div
            className={`flex gap-3 p-4 ${isRecording ? "items-center" : "items-end"}`}
          >
            <div className="flex-1 min-w-0">
              {showSpeechToText && (
                <div
                  className={isRecording ? "block" : "hidden"}
                  style={{ transform: "scaleY(2)" }}
                >
                  <VoiceVisualizer
                    controls={recorderControls}
                    height={50}
                    width="100%"
                    backgroundColor="transparent"
                    mainBarColor={primaryColor}
                    barWidth={2}
                    gap={1}
                    rounded={5}
                    isControlPanelShown={false}
                    isDefaultUIShown={false}
                    isDownloadAudioButtonShown={false}
                    isProgressIndicatorShown={false}
                    isProgressIndicatorTimeShown={false}
                    isProgressIndicatorOnHoverShown={false}
                    isProgressIndicatorTimeOnHoverShown={false}
                    isAudioProcessingTextShown={false}
                    fullscreen={true}
                    speed={2}
                  />
                </div>
              )}
              {!isRecording && (
                <>
                  <ChatTextarea
                    value={value}
                    onChange={onChange}
                    onSend={onSend}
                    disabled={disabled || isLoading}
                    canSend={!isSendDisabled && !isLoading}
                    placeholder={placeholder}
                  />
                  {isICRAResearchAssist || isArmada ? null : (
                    <div className="flex gap-2">
                      {isPWC || !showUpload ? null : (
                        <AttachmentButton disabled={isAttachmentDisabled} />
                      )}
                      {showWebSearch && !isMSquared && (
                        <WebSearchToggle disabled={disabled} />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <AskneoServiceSelector />
              {showSpeechToText && (
                <SpeechButton
                  speechState={speechState}
                  onStart={startSpeech}
                  onStop={stopSpeech}
                  onCancel={cancelSpeech}
                  disabled={isStreaming}
                  isError={speechError}
                />
              )}
              {!isRecording && (
                <SendButton
                  onSend={onSend}
                  onStop={onStop}
                  isStreaming={isStreaming}
                  disabled={isSendDisabled || isLoading || !value.trim()}
                  disabledReason={getDisabledReason()}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ChatInputCard;

type ChatInputCardProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
};
